import { useContext, useLayoutEffect } from 'react'
import PrimeReact, { PrimeReactContext } from 'primereact/api'
import { ZIndexUtils } from 'primereact/utils'

// PrimeReact's own stacking elements: dialog masks and dialogs, overlay panels and the overlay mask
// class they share. Used to find what is actually on screen, independent of any module's stack.
const PRIME_LAYER_SELECTOR = '.p-dialog-mask, .p-component-overlay, .p-dialog, .p-overlaypanel, .p-sidebar-mask, .p-sidebar'

const highestRenderedLayer = element => {
  let highest = 0
  document.querySelectorAll(PRIME_LAYER_SELECTOR).forEach(layer => {
    if (layer === element || layer.contains(element)) return
    highest = Math.max(highest, parseInt(window.getComputedStyle(layer).zIndex, 10) || 0)
  })
  return highest
}

// Stacks `elementRef` (a portaled, fixed-position popover or tooltip) above every PrimeReact
// dialog, the way PrimeReact's own overlays do. A Dialog mask takes an auto-incrementing z-index
// (1101 on a fresh page, over 3000 after several dialogs), so a static CSS z-index can end up
// behind the dialog it was opened from.
//
// While `active`, the element takes the next 'overlay' layer from PrimeReact's ZIndexUtils stack,
// but never less than 1 above the highest PrimeReact layer actually rendered in the DOM. The DOM
// check matters when the Dialog and this module do not share one ZIndexUtils instance: the Vite
// dev optimizer can serve two optimize passes of primereact, giving each its own stack, so ours
// would start from its base (1001) and sit under the dialog's 1101 mask. The ZIndexUtils layer is
// released when the element goes inactive or unmounts.
export const useOverlayZIndex = (active, elementRef) => {
  const context = useContext(PrimeReactContext)

  useLayoutEffect(() => {
    const element = elementRef.current
    if (!active || !element) return undefined
    ZIndexUtils.set('overlay', element, context?.autoZIndex ?? PrimeReact.autoZIndex, context?.zIndex?.overlay ?? PrimeReact.zIndex.overlay)
    const allocated = ZIndexUtils.get(element)
    element.style.zIndex = String(Math.max(allocated, highestRenderedLayer(element) + 1))
    return () => {
      // clear() releases the layer by its value, so restore the allocated one first.
      element.style.zIndex = String(allocated)
      ZIndexUtils.clear(element)
    }
  }, [active, elementRef, context])
}
